// Signing section component - email signing form, proof type selection, and status
// Frontend developers can modify the visual appearance here

import type { UseSigningFlowReturn } from "../hooks/useSigningFlow";

interface SigningSectionProps {
  signing: UseSigningFlowReturn;
}

export function SigningSection({ signing }: SigningSectionProps) {
  const {
    email,
    setEmail,
    proofType,
    setProofType,
    registerOnChain,
    setRegisterOnChain,
    signingState,
    signingStatus,
    signingError,
    sendEmailNotification,
    setSendEmailNotification,
    emailNotificationsAvailable,
    downloadingEmail,
    downloadingProof,
    handleSign,
    handleDownloadWithEmail,
    handleDownloadWithProof,
    isValidEmail,
  } = signing;

  return (
    <div className="signing-section">
      <h2>Sign for Provenance</h2>
      <p className="signing-description">
        Sign this hash with your email to create a verifiable proof of
        authorship using ZK Email.
      </p>

      {signingState === "pending" || signingState === "success" ? (
        <SigningStatus
          email={email}
          signingStatus={signingStatus}
          registerOnChain={registerOnChain}
          proofType={proofType}
          downloadingEmail={downloadingEmail}
          downloadingProof={downloadingProof}
          onDownloadWithEmail={handleDownloadWithEmail}
          onDownloadWithProof={handleDownloadWithProof}
        />
      ) : (
        <SigningForm
          email={email}
          onEmailChange={setEmail}
          proofType={proofType}
          onProofTypeChange={setProofType}
          registerOnChain={registerOnChain}
          onRegisterOnChainChange={setRegisterOnChain}
          sendEmailNotification={sendEmailNotification}
          onSendEmailNotificationChange={setSendEmailNotification}
          emailNotificationsAvailable={emailNotificationsAvailable}
          signingState={signingState}
          signingError={signingError}
          onSign={handleSign}
          isValidEmail={isValidEmail}
        />
      )}
    </div>
  );
}

// Sub-component: Signing status after email is sent
interface SigningStatusProps {
  email: string;
  signingStatus: UseSigningFlowReturn["signingStatus"];
  registerOnChain: boolean;
  proofType: UseSigningFlowReturn["proofType"];
  downloadingEmail: boolean;
  downloadingProof: boolean;
  onDownloadWithEmail: () => void;
  onDownloadWithProof: () => void;
}

function SigningStatus({
  email,
  signingStatus,
  registerOnChain,
  proofType,
  downloadingEmail,
  downloadingProof,
  onDownloadWithEmail,
  onDownloadWithProof,
}: SigningStatusProps) {
  return (
    <div className="success-message">
      <div className="success-icon">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22,4 12,14.01 9,11.01" />
        </svg>
      </div>
      <h3>Check Your Email!</h3>
      <p>
        We've sent a confirmation email to <strong>{email}</strong>.
      </p>
      <div className="instructions">
        <p>
          <strong>To complete the signing:</strong>
        </p>
        <ol>
          <li>Open the email from ZK Email</li>
          <li>
            Reply to the email with any word like <code>confirm</code>
          </li>
          {registerOnChain && (
            <li>Your signature will be registered on-chain</li>
          )}
        </ol>
      </div>

      {/* Status display */}
      <div className="signing-status">
        <p>
          <strong>Status:</strong>{" "}
          {signingStatus?.status || "Waiting for email reply..."}
        </p>
        {signingStatus?.tx_hash && (
          <p className="tx-hash">
            <strong>Transaction:</strong>{" "}
            <a
              href={`https://sepolia.etherscan.io/tx/${signingStatus.tx_hash}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {signingStatus.tx_hash.slice(0, 10)}...
              {signingStatus.tx_hash.slice(-8)}
            </a>
          </p>
        )}
      </div>

      {/* Download Section */}
      <div className="download-section">
        {proofType === "direct" ? (
          <>
            <button
              className="download-button"
              onClick={onDownloadWithEmail}
              disabled={downloadingEmail || !signingStatus?.raw_email}
            >
              {downloadingEmail
                ? "Preparing..."
                : !signingStatus?.raw_email
                  ? "Waiting for email..."
                  : "Download with Email Signature"}
            </button>
            <p className="download-hint">
              Image with embedded raw email (available after you reply)
            </p>
          </>
        ) : (
          <>
            <button
              className="download-button download-button-proof"
              onClick={onDownloadWithProof}
              disabled={
                downloadingProof ||
                !signingStatus?.proof ||
                (registerOnChain && !signingStatus?.tx_hash)
              }
            >
              {downloadingProof
                ? "Preparing..."
                : !signingStatus?.proof
                  ? "Waiting for ZK proof..."
                  : registerOnChain && !signingStatus?.tx_hash
                    ? "Waiting for transaction..."
                    : "Download with ZK Proof"}
            </button>
            <p className="download-hint">
              Image with embedded ZK proof{" "}
              {registerOnChain
                ? "(available after on-chain registration)"
                : "(available after proof generation)"}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// Sub-component: Signing form before signing
interface SigningFormProps {
  email: string;
  onEmailChange: (email: string) => void;
  proofType: UseSigningFlowReturn["proofType"];
  onProofTypeChange: (type: UseSigningFlowReturn["proofType"]) => void;
  registerOnChain: boolean;
  onRegisterOnChainChange: (value: boolean) => void;
  sendEmailNotification: boolean;
  onSendEmailNotificationChange: (value: boolean) => void;
  emailNotificationsAvailable: boolean;
  signingState: UseSigningFlowReturn["signingState"];
  signingError: string | null;
  onSign: () => void;
  isValidEmail: (email: string) => boolean;
}

function SigningForm({
  email,
  onEmailChange,
  proofType,
  onProofTypeChange,
  registerOnChain,
  onRegisterOnChainChange,
  sendEmailNotification,
  onSendEmailNotificationChange,
  emailNotificationsAvailable,
  signingState,
  signingError,
  onSign,
  isValidEmail,
}: SigningFormProps) {
  return (
    <>
      {/* Proof Type Selection */}
      <div className="proof-type-selection">
        <h3>Choose Proof Type</h3>
        <div className="proof-options">
          <label
            className={`proof-option ${proofType === "direct" ? "selected" : ""}`}
          >
            <input
              type="radio"
              name="proofType"
              value="direct"
              checked={proofType === "direct"}
              onChange={() => {
                onProofTypeChange("direct");
                onRegisterOnChainChange(false);
              }}
              disabled={signingState === "signing"}
            />
            <div className="proof-option-content">
              <div className="proof-option-header">
                <strong>DKIM Signature</strong>
              </div>
              <div className="proof-option-description">
                <p>Uses raw email as signature</p>
              </div>
            </div>
          </label>

          <label
            className={`proof-option ${proofType === "zk" ? "selected" : ""}`}
          >
            <input
              type="radio"
              name="proofType"
              value="zk"
              checked={proofType === "zk"}
              onChange={() => onProofTypeChange("zk")}
              disabled={signingState === "signing"}
            />
            <div className="proof-option-content">
              <div className="proof-option-header">
                <strong>ZK Proof</strong>
              </div>
              <div className="proof-option-description">
                <p>Uses ZK proof to verify signature</p>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* On-Chain Registration (only shown for ZK proof) */}
      {proofType === "zk" && (
        <div className="onchain-registration">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={registerOnChain}
              onChange={(e) => onRegisterOnChainChange(e.target.checked)}
              disabled={signingState === "signing"}
            />
            <div className="checkbox-content">
              <strong>Register signature on-chain</strong>
              <p>
                Allows others to check authorship of your image with verifiable
                timestamp
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Email Notification (only shown if SMTP is configured) */}
      {emailNotificationsAvailable && proofType && (
        <div className="email-notification-option">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={sendEmailNotification}
              onChange={(e) => onSendEmailNotificationChange(e.target.checked)}
              disabled={signingState === "signing"}
            />
            <div className="checkbox-content">
              <strong>Send signed image to my email</strong>
              <p>Receive the signed image as an email attachment when ready</p>
            </div>
          </label>
        </div>
      )}

      <div className="email-input-group">
        <input
          type="email"
          placeholder="Enter your email address"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          className="email-input"
          disabled={signingState === "signing"}
        />
        <button
          className="sign-button"
          onClick={onSign}
          disabled={
            !isValidEmail(email) || !proofType || signingState === "signing"
          }
        >
          {signingState === "signing" ? "Sending..." : "Sign with Email"}
        </button>
      </div>
      {signingError && <div className="error">{signingError}</div>}
    </>
  );
}
